import { inject, injectable } from 'inversify';
import {
  AddToCartParams,
  CartRepositoryInterface,
  GetCartParams,
  RemoveFromCartParams,
  CreateUserCartParams,
  GetCartByUserIdParams,
  MigrateCartProductsParams,
} from '../domain/CartRepositoryInterface';
import CartService from 'src/features/cart/data/service/CartService';
import AuthService from 'src/features/auth/data/service/authService';
import { Cart, LocalStorageKeys, AppSubdomains } from 'src/types/common';
import ProductsRepository from 'src/features/products/data/ProductsRepository';
import {
  getLocalStorageItemWithExpiry,
  removeLocalStorageItem,
  getLocalStorageItem,
} from 'src/utils/localStorage';
import { getStorageSession } from 'src/utils/localStorage/session';
import { getSubdomain } from 'src/utils/domain/getSubdomain';

@injectable()
class CartRepository implements CartRepositoryInterface {
  @inject(CartService) private readonly cartService!: CartService;
  @inject(AuthService) private readonly authService!: AuthService;
  @inject(ProductsRepository)
  private readonly productsRepository!: ProductsRepository;

  async createUserCart(params: CreateUserCartParams) {
    const response = await this.cartService.createCart(params);

    return response.data;
  }

  async getCart(params: GetCartParams) {
    const subdomain = getSubdomain(AppSubdomains);
    const storedToken =
      subdomain === AppSubdomains.Terea
        ? getLocalStorageItem(LocalStorageKeys.VerificationToken)
        : null;

    const response = await this.cartService.getCart({
      ...params,
      ...(storedToken && { token: storedToken }),
    });

    return response.data;
  }

  async getCartByUserId(params: GetCartByUserIdParams): Promise<Cart> {
    const { data: userDetails } = await this.authService.getUserById({
      token: params.accessToken,
      user_pk: params.userId,
    });
    const mainAddress = userDetails.main_address;
    const storeSlugName = mainAddress?.store_data?.slug_name;

    if (!storeSlugName) {
      throw new Error('El usuario no tiene una tienda asignada');
    }

    if (!userDetails.active_cart) {
      const newCart = await this.createUserCart({
        storeSlugName,
        addressId: mainAddress.pk,
        accessToken: params.accessToken,
      });

      await this.checkAndAddAnonymousWantedProduct({
        cartId: newCart.pk,
        storeSlugName,
      });

      return newCart;
    }

    await this.checkAndAddAnonymousWantedProduct({
      cartId: userDetails.active_cart,
      storeSlugName: storeSlugName,
    });

    const subdomain = getSubdomain(AppSubdomains);
    const storedToken =
      subdomain === AppSubdomains.Terea
        ? getLocalStorageItem(LocalStorageKeys.VerificationToken)
        : null;

    const response = await this.cartService.getCart({
      cartId: userDetails.active_cart,
      accessToken: params.accessToken,
      ...(storedToken && { token: storedToken }),
    });

    return response.data;
  }

  async addToCart(params: AddToCartParams) {
    const response = await this.cartService.addToCart(params);

    return response.data;
  }

  async removeFromCart(params: RemoveFromCartParams) {
    const response = await this.cartService.removeFromCart(params);

    return response.data;
  }

  async migrateCartProducts({
    prevCartItems,
    newCart,
    storeSlugName,
    accessToken,
  }: MigrateCartProductsParams) {
    return this.productsRepository
      .getStoreStock({
        storeSlug: storeSlugName,
        limit: 5000,
        token: accessToken,
      })
      .then((storeStock) => {
        const promises: Promise<Cart>[] = [];

        storeStock.results.forEach((stockProduct) => {
          const cartProduct = prevCartItems.find(
            (cp) => cp.product.pk === stockProduct.product.pk
          );
          if (cartProduct && stockProduct.quantity > 0) {
            const quantityToAdd = Math.min(
              cartProduct.quantity,
              stockProduct.quantity
            );
            promises.push(
              this.addToCart({
                productId: stockProduct.product.pk,
                quantity: quantityToAdd,
                price: Number(cartProduct.price),
                accessToken: accessToken,
                cartId: newCart.pk,
              })
            );
          }
        });

        if (!promises.length) {
          return [];
        }

        return Promise.all(promises);
      });
  }

  private async checkAndAddAnonymousWantedProduct(params: {
    cartId: number;
    storeSlugName: string;
  }) {
    const wantedProduct = getLocalStorageItemWithExpiry(
      LocalStorageKeys.AnonymousWantedProduct
    );

    if (!wantedProduct) return;

    const session = getStorageSession();

    if (!session?.accessToken) return;

    const storeStock = await this.productsRepository.getStoreStock({
      storeSlug: params.storeSlugName,
      limit: 5000,
      token: session.accessToken,
    });

    const stockProduct = storeStock.results.find(
      (sp) => sp.product.pk === wantedProduct.pk
    );

    if (!stockProduct || !stockProduct.quantity) {
      return;
    }

    await this.addToCart({
      productId: stockProduct.product.pk,
      quantity: 1,
      price: Number(stockProduct.price),
      accessToken: session.accessToken,
      cartId: params.cartId,
    });

    removeLocalStorageItem(LocalStorageKeys.AnonymousWantedProduct);
  }
}

export default CartRepository;
